<template>
    <VJoyHighlight
        v-if="visibilityAlertsHandledArray.length > 0"
        level="warning"
        icon="warning-triangle"
        display-icon
        data-testid="profile-alert-section"
        class="profile-alert-section"
    >
        {{ $t('profile.visibility.alert') }}
        <template v-for="(alert, i) in visibilityAlertsHandledArray" :key="i">
            <template v-if="alert.field">
                <span v-if="alert.field === 'TOO_MANY_REFUSAL'">
                    <VJoyLink href="/inbox" :text="alertMessage(alert)" />
                </span>
                <span v-else-if="alert.field === 'PHOTO'">
                    <VJoyLink href="#" :text="alertMessage(alert)" @click="openPhotoEdition" />
                </span>
                <span v-else-if="alert.field === 'DESCRIPTION'">
                    <VJoyLink href="#" :text="alertMessage(alert)" @click="openDescriptionEdition" />
                </span>
                <span v-else-if="alert.field === 'PRICE'">
                    <VJoyLink href="#" :text="alertMessage(alert)" @click="openPriceEdition" />
                </span>
                <span v-else-if="alert.field === 'HEADLINE'">
                    <VJoyLink href="#" :text="alertMessage(alert)" @click="openHeadlineEdition" />
                </span>
                <span v-else-if="alert.field === 'HIDDEN_PRICE'">
                    <VJoyLink href="#" :text="alertMessage(alert)" @click="openPriceEdition" />
                </span>
                <span v-else-if="alert.field === 'LOCATION'">
                    <VJoyLink href="#" :text="alertMessage(alert)" @click="openLocationEdition" />
                </span>
                <span v-else-if="alert.field === 'AVAILABILITY'" @click="openAvailabilityEdition">
                    <VJoyLink href="#" :text="alertMessage(alert)" />
                </span>
                <span v-else>
                    {{ alertMessage(alert) }}
                </span>
            </template>
            <span v-else-if="alert.rawMessage.indexOf('no.experience') > -1">
                <VJoyLink href="#" :text="alertMessage(alert)" @click="openExperienceEdition" />
            </span>
            <span v-else-if="alert.rawMessage.indexOf('no.education') > -1">
                <VJoyLink href="#educationSection" :text="alertMessage(alert)" />
            </span>
            <span v-else-if="alert.rawMessage.indexOf('ask.recommandations') > -1">
                <VJoyLink href="#" :text="alertMessage(alert)" @click="openRecommendationRequestEdition" />
            </span>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-else v-html="t(alert.rawMessage as TranslationKey)"></span>
            <span v-if="i < visibilityAlertsHandledArray.length - 1">{{ ', ' }}</span>
        </template>
    </VJoyHighlight>

    <div v-if="freelanceCharter?.invalidated">
        <VJoyHighlight level="error" display-icon>
            <div>
                <p class="profile-moderation-alert__title" v-html="$t('profile.alert.moderation.banner', {0: charterInvalidUntilFormatted})"></p>
            </div>
        </VJoyHighlight>
    </div>
</template>
<script setup lang="ts">
    import {navbarEventBus} from '@navbar/public-api';
    import {formatISO, parseISO} from 'date-fns';
    import {storeToRefs} from 'pinia';
    import {computed, onMounted, onUnmounted, useTranslation} from '#imports';
    import {VJoyHighlight, VJoyLink} from '@maltjoy/core-vue';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint, RecommendationRequestStartedEntryPoint} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import type {VisibilityAlertResource} from '~/types/api-raw-types';
    import type {TranslationKey} from '#build/i18n';

    const {t} = useTranslation();
    const profileStore = useProfileStore();
    const {getVisibility, getFreelanceCharter} = useProfileStore();
    const {profile} = storeToRefs(profileStore);

    if (profile.value?.editMode) {
        await refreshVisibility();
        await getFreelanceCharter();
    }
    const {visibility, freelanceCharter} = storeToRefs(profileStore);

    const visibilityAlertsFieldHandled = ['AVAILABILITY', 'PRICE', 'PHOTO', 'HIDDEN_PRICE', 'MESSAGE'];

    const visibilityAlertsHandledArray = computed(() => {
        return visibility.value?.alerts.filter((alert) => visibilityAlertsFieldHandled.includes(alert.field)) || [];
    });

    async function refreshVisibility() {
        await getVisibility();
    }

    // FIXME after the profile migration, I strongly recommend to avoid using v-html
    // TO do that, we should change all messages
    // our resource bundle to remove html // or just use the key as a message (in raw message)
    function alertMessage(alert: VisibilityAlertResource) {
        const message = t(`${alert.rawMessage}` as TranslationKey);
        return message.replace(/(<([^>]+)>)/gi, '');
    }

    const charterInvalidUntilFormatted = computed(() => {
        if (freelanceCharter.value?.invalidated) {
            return formatISO(parseISO(freelanceCharter.value?.invalidUntil), {representation: 'date'});
        }
        return '';
    });

    function openHeadlineEdition() {
        eventBus.emit('header:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_POPUP});
    }

    function openDescriptionEdition() {
        eventBus.emit('about:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_POPUP});
    }

    function openLocationEdition() {
        eventBus.emit('header:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_POPUP});
    }

    function openRecommendationRequestEdition() {
        eventBus.emit('recommendations:edit-mode', {entryPoint: RecommendationRequestStartedEntryPoint.ALERT_SECTION});
    }

    function openPriceEdition() {
        eventBus.emit('header:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_POPUP});
    }

    function openExperienceEdition() {
        eventBus.emit('experiences:selection-mode');
    }

    function openPhotoEdition() {
        eventBus.emit('photo:edit-mode', {entryPoint: EditionOpenedEntryPoint.PROFILE_POPUP});
    }

    function openAvailabilityEdition() {
        navbarEventBus.emit('open-availibility-dialog', {source: 'CLICK_ON_PROFILE_ALERT'});
    }

    onMounted(() => {
        eventBus.on('profile:updated', refreshVisibility);
    });

    onUnmounted(() => {
        eventBus.off('profile:updated', refreshVisibility);
    });
</script>
<style lang="scss" scoped>
    .profile-alert-section {
        color: var(--joy-color-text-body);

        a {
            border: none;
            text-decoration: none;
        }
    }
</style>
